import { useTranslation } from 'react-i18next';

import { Nfc } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';

import useNfcReader from 'hooks/useNfcReader';

type Props = {
	label: string;
	/** Receives the normalized UID of the card read. The read stops right before. */
	onUid: (uid: string) => void;
	disabled?: boolean;
	/** A big button, full width on a phone, for the main action of a screen. */
	large?: boolean;
};

/**
 * Reads one card when clicked, and hands over its UID. While it waits for the card the same
 * button cancels the read.
 *
 * Renders nothing where the browser cannot read NFC.
 */
export default function NfcReadButton({ label, onUid, disabled, large }: Props) {
	const { t } = useTranslation();
	const { available, scanning, error, start, stop } = useNfcReader({ onUid });

	if (!available) return null;

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', alignItems: large ? 'stretch' : 'flex-start' }}>
			<Button
				variant={large ? 'contained' : 'outlined'}
				size={large ? 'large' : 'medium'}
				startIcon={<Nfc />}
				onClick={scanning ? stop : start}
				disabled={disabled && !scanning}
				color={scanning ? 'secondary' : 'primary'}
				sx={large ? { minHeight: 48 } : { whiteSpace: 'nowrap' }}
			>
				{scanning ? t('nfc:scanWaitingCancel') : label}
			</Button>
			{!!error && (
				<Typography variant="caption" color="error">
					{error}
				</Typography>
			)}
		</Box>
	);
}
